import React, { useState, useRef } from 'react';
import toast from 'react-hot-toast';
import { ENV } from '../../config/env';

interface TrackingCodeGeneratorProps {
  programId?: string | null;
  programName: string;
  cookieDurationDays?: number;
}

const TrackingCodeGenerator: React.FC<TrackingCodeGeneratorProps> = ({ programId, programName, cookieDurationDays }) => {
  const [activeTab, setActiveTab] = useState<'js' | 'pixel'>('js');
  const jsCodeRef = useRef<HTMLTextAreaElement>(null);
  const pixelCodeRef = useRef<HTMLTextAreaElement>(null);
  const programToken = programId ?? 'PROGRAM_ID';
  const cookieDays = typeof cookieDurationDays === 'number' && cookieDurationDays > 0 ? cookieDurationDays : 30;
  const apiBase = `${ENV.API_URL}/api/affiliate`;
  
  // Generate the JavaScript tracking code
  const generateJsCode = (): string => {
    return `<!-- Explanatum Affiliate Tracking Code for ${programName} -->
<script>
  (function() {
    // Get referral code from URL parameters
    function getRefCode() {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('ref');
    }

    function getUtmParams() {
      const urlParams = new URLSearchParams(window.location.search);
      return {
        utm_source: urlParams.get('utm_source') || '',
        utm_medium: urlParams.get('utm_medium') || '',
        utm_campaign: urlParams.get('utm_campaign') || '',
        utm_term: urlParams.get('utm_term') || '',
        utm_content: urlParams.get('utm_content') || ''
      };
    }

    function saveUtmParams(utm) {
      try {
        window.localStorage.setItem('aff_utm_${programToken}', JSON.stringify(utm || {}));
      } catch (e) {
        // ignore
      }
    }

    function loadUtmParams() {
      try {
        const raw = window.localStorage.getItem('aff_utm_${programToken}');
        return raw ? JSON.parse(raw) : {};
      } catch (e) {
        return {};
      }
    }
    
    // Set tracking cookie
    function setTrackingCookie(refCode) {
      const expires = new Date();
      expires.setDate(expires.getDate() + ${cookieDays});
      const secure = window.location.protocol === 'https:' ? ';Secure' : '';
      document.cookie = \`aff_ref_\${encodeURIComponent('${programToken}')}=\${refCode};expires=\${expires.toUTCString()};path=/;SameSite=Lax\${secure}\`;
    }
    
    // Track conversion (call this function during checkout/conversion)
    window.trackAffiliateConversion = function(orderId, amount) {
      const refCode = getRefFromCookie() || getRefCode();
      if (!refCode) return;

      const utm = loadUtmParams();
      
      fetch('${apiBase}/track-conversion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          program_id: '${programToken}',
          referral_code: refCode,
          order_id: orderId,
          amount: amount,
          utm_source: utm.utm_source || '',
          utm_medium: utm.utm_medium || '',
          utm_campaign: utm.utm_campaign || '',
          utm_term: utm.utm_term || '',
          utm_content: utm.utm_content || ''
        })
      })
      .catch(err => console.error('Error tracking affiliate conversion:', err));
    }
    
    // Get referral code from cookie
    function getRefFromCookie() {
      const cookies = document.cookie.split(';');
      const cookieName = \`aff_ref_\${encodeURIComponent('${programToken}')}\`;
      
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.startsWith(cookieName + '=')) {
          return cookie.substring(cookieName.length + 1);
        }
      }
      return null;
    }
    
    // Track the click/visit
    function trackClick(refCode) {
      if (!refCode) return;

      const utm = getUtmParams();
      if (utm.utm_source || utm.utm_medium || utm.utm_campaign || utm.utm_term || utm.utm_content) {
        saveUtmParams(utm);
      }
      
      fetch('${apiBase}/track-click', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          program_id: '${programToken}',
          referral_code: refCode,
          utm_source: utm.utm_source || '',
          utm_medium: utm.utm_medium || '',
          utm_campaign: utm.utm_campaign || '',
          utm_term: utm.utm_term || '',
          utm_content: utm.utm_content || ''
        })
      })
      .catch(err => console.error('Error tracking affiliate click:', err));
    }
    
    // Initialize tracking
    const refCode = getRefCode();
    if (refCode) {
      setTrackingCookie(refCode);
      trackClick(refCode);
    }
  })();
</script>`;
  };
  
  // Generate the image pixel tracking code (simpler fallback)
  const generatePixelCode = (): string => {
    return `<!-- Explanatum Affiliate Tracking Pixel for ${programName} -->
<img src="${apiBase}/pixel-track?pid=${programToken}&ref=AFFILIATE_CODE" width="1" height="1" style="display:none" alt="" />

<!-- Note: Replace AFFILIATE_CODE with the actual referral code or use dynamic code insertion -->`;
  };
  
  // Copy code to clipboard
  const handleCopy = async (type: 'js' | 'pixel') => {
    if (!programId) {
      toast.error('Create a program to generate a program-specific tracking code.');
      return;
    }
    const ref = type === 'js' ? jsCodeRef : pixelCodeRef;
    
    if (ref.current) {
      const text = ref.current.value;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
        } else {
          const el = document.createElement('textarea');
          el.value = text;
          el.setAttribute('readonly', '');
          el.style.position = 'fixed';
          el.style.top = '0';
          el.style.left = '-9999px';
          el.style.opacity = '0';
          document.body.appendChild(el);
          el.focus();
          el.select();
          toast.success('Code selected. Press Ctrl+C (or Cmd+C) to copy.');
          window.setTimeout(() => {
            try {
              document.body.removeChild(el);
            } catch (e) {
              // ignore
            }
          }, 5000);
          return;
        }
        toast.success(`${type === 'js' ? 'JavaScript' : 'Pixel'} code copied to clipboard!`);
      } catch (e) {
        console.error(e);
        toast.error('Failed to copy to clipboard');
      }
    }
  };
  
  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          Tracking Code Generator
        </h3>
        <div className="mt-2 max-w-xl text-sm text-gray-500">
          <p>
            Generate tracking code to place on your website. This allows you to track affiliate referrals and commissions.
          </p>
        </div>
        {!programId && (
          <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 p-3 text-blue-800 text-sm">
            No program detected. The snippets below use a placeholder <strong>PROGRAM_ID</strong>. Create a program to get a real program ID.
          </div>
        )}
        
        <div className="mt-5 border-b border-gray-200">
          <div className="flex">
            <button
              onClick={() => setActiveTab('js')}
              className={`py-4 px-6 font-medium text-sm ${
                activeTab === 'js'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              JavaScript Tracking
            </button>
            <button
              onClick={() => setActiveTab('pixel')}
              className={`py-4 px-6 font-medium text-sm ${
                activeTab === 'pixel'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Image Pixel Fallback
            </button>
          </div>
        </div>
        
        {activeTab === 'js' ? (
          <div className="mt-6">
            <h4 className="text-sm font-medium text-gray-900">JavaScript Tracking Code</h4>
            <p className="mt-1 text-sm text-gray-500">
              Place this code in the <code className="bg-gray-100 px-1 py-0.5 rounded">&lt;head&gt;</code> section of your website.
            </p>
            <div className="mt-3 relative">
              <textarea
                ref={jsCodeRef}
                readOnly
                rows={10}
                className="block w-full border border-gray-300 rounded-md shadow-sm p-3 text-xs font-mono bg-gray-50"
                value={generateJsCode()}
              />
              <button
                type="button"
                onClick={() => handleCopy('js')}
                className="absolute top-2 right-2 inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
              >
                Copy
              </button>
            </div>
            
            <div className="mt-6 p-4 bg-blue-50 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    How to implement conversion tracking
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>
                      To track conversions (purchases), call the following function when a purchase is completed:
                    </p>
                    <pre className="bg-white mt-2 p-2 rounded text-xs overflow-x-auto">
                      {`window.trackAffiliateConversion('ORDER_ID', ORDER_AMOUNT);`}
                    </pre>
                    <p className="mt-2">
                      Place this in your order confirmation page or checkout success handler.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-6">
            <h4 className="text-sm font-medium text-gray-900">Image Pixel Tracking Code</h4>
            <p className="mt-1 text-sm text-gray-500">
              Use this simpler tracking method if you can't implement JavaScript tracking.
            </p>
            <div className="mt-3 relative">
              <textarea
                ref={pixelCodeRef}
                readOnly
                rows={5}
                className="block w-full border border-gray-300 rounded-md shadow-sm p-3 text-xs font-mono bg-gray-50"
                value={generatePixelCode()}
              />
              <button
                type="button"
                onClick={() => handleCopy('pixel')}
                className="absolute top-2 right-2 inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
              >
                Copy
              </button>
            </div>
            
            <div className="mt-6 p-4 bg-yellow-50 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Important Note
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>
                      The pixel tracking method is less reliable than JavaScript tracking. It doesn't support cookies for persistent tracking across multiple visits.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="mt-8 border-t border-gray-200 pt-6">
          <h4 className="text-sm font-medium text-gray-900">Platform-specific Integration Guides</h4>
          
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="border border-gray-200 rounded-md p-4">
              <h5 className="text-sm font-medium text-gray-900">WordPress</h5>
              <p className="mt-1 text-sm text-gray-500">
                Add the tracking code to your theme's header.php file or use a plugin like "Insert Headers and Footers".
              </p>
            </div>
            <div className="border border-gray-200 rounded-md p-4">
              <h5 className="text-sm font-medium text-gray-900">Shopify</h5>
              <p className="mt-1 text-sm text-gray-500">
                Go to Online Store &gt; Themes &gt; Actions &gt; Edit code &gt; Add code to theme.liquid file.
              </p>
            </div>
            <div className="border border-gray-200 rounded-md p-4">
              <h5 className="text-sm font-medium text-gray-900">Wix</h5>
              <p className="mt-1 text-sm text-gray-500">
                Go to Settings &gt; Advanced &gt; Custom Code and add the tracking code to the Head section.
              </p>
            </div>
            <div className="border border-gray-200 rounded-md p-4">
              <h5 className="text-sm font-medium text-gray-900">Squarespace</h5>
              <p className="mt-1 text-sm text-gray-500">
                Go to Settings &gt; Advanced &gt; Code Injection and add the tracking code to the Header section.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackingCodeGenerator;
